'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { LinkButton } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  cardVariants,
  reducedMotionVariants,
  gridContainerVariants,
} from '../../lib/motion'

export interface FeaturesStickyProps {
  /** Section heading */
  heading?: string
  /** Section description */
  description?: string
  /** Array of feature items */
  features?: Array<{
    /** Small uppercase tag above title */
    tagline: string
    /** Feature description text */
    description: string
    /** Button text */
    buttonText?: string
    /** Button URL */
    buttonUrl?: string
  }>
  className?: string
}

/**
 * FeaturesSticky - Sticky header with scrolling feature cards
 *
 * Split layout with heading/description sticky on left (lg screens)
 * and scrolling feature cards on right.
 */
const FeaturesSticky = ({
  heading = 'Build Better Products',
  description = 'Everything you need to create exceptional user experiences. From design to deployment, we have you covered.',
  features = [
    {
      tagline: 'Design System',
      description:
        'A comprehensive collection of reusable components built with accessibility and customization in mind.',
      buttonText: 'Learn more',
      buttonUrl: '#',
    },
    {
      tagline: 'Developer Experience',
      description:
        'Type-safe APIs with full TypeScript support. Autocomplete everything and catch errors before runtime.',
      buttonText: 'Learn more',
      buttonUrl: '#',
    },
    {
      tagline: 'Production Ready',
      description:
        'Battle-tested components used by thousands of developers. Ship with confidence knowing everything just works.',
      buttonText: 'Learn more',
      buttonUrl: '#',
    },
  ],
  className,
}: FeaturesStickyProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const cardAnimation = prefersReducedMotion ? reducedMotionVariants : cardVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto grid gap-y-12 md:grid-cols-12 md:gap-x-6 md:gap-y-0">
        {/* Left side - sticky on lg screens */}
        <motion.div
          className="md:col-span-6 lg:col-span-5 lg:sticky lg:top-32 lg:self-start"
          variants={headerAnimation}
        >
          <h2 className="mb-3 text-xl font-semibold md:mb-4 md:text-4xl lg:mb-6">
            {heading}
          </h2>
          <Markdown size="base">{description}</Markdown>
        </motion.div>

        {/* Right side - scrolling cards */}
        <motion.div
          className="grid gap-y-5 md:col-span-6 md:gap-y-[1.875rem] lg:col-start-7"
          variants={gridContainerVariants(0.15, 0.2)}
        >
          {features.map((feature) => (
            <motion.div
              key={feature.tagline}
              className="flex flex-col justify-center overflow-clip rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-card px-4 py-6 md:px-8 md:py-10 lg:px-10 lg:py-12"
              variants={cardAnimation}
            >
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {feature.tagline}
              </p>
              <p className="mb-8 font-semibold text-foreground lg:text-xl">
                {feature.description}
              </p>
              {feature.buttonText && (
                <LinkButton href={feature.buttonUrl || '#'}>
                  {feature.buttonText}
                </LinkButton>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  )
}

export { FeaturesSticky }
